import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Initialize Stripe
const stripePromise = loadStripe('pk_live_51S04JcQgLAzH7rkp6qDULJehVsdPYMl1csil7Y0qbz9XLg1xw03oOIXFLyKm6b5ltYl2bMk4xozSrebbDb3kUNBv00kULROXkg');

interface PaymentFormProps {
  planType: 'monthly' | 'yearly';
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ planType, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !user) {
      return;
    }

    setLoading(true);

    try {
      // Create payment intent
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke(
        'create_payment_intent_live_2025_10_22_08_00',
        {
          body: {
            planType,
            userId: user.id,
          },
        }
      );

      if (paymentError) {
        throw new Error(paymentError.message);
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        paymentData.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: user.email,
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent?.status === 'succeeded') {
        // Confirm payment on backend
        const { error: confirmError } = await supabase.functions.invoke(
          'confirm_payment_live_2025_10_22_08_00',
          {
            body: {
              paymentIntentId: paymentIntent.id,
              userId: user.id,
              planType,
            },
          }
        );

        if (confirmError) {
          console.warn('Payment succeeded but confirmation failed:', confirmError);
        }

        toast({
          title: "Payment Successful!",
          description: `Your ${planType} subscription is now active.`,
        });

        onSuccess();
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Failed",
        description: error.message || "An error occurred during payment processing.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const price = planType === 'monthly' ? '$29' : '$290';
  const savings = planType === 'yearly' ? 'Save $58 (2 months free!)' : '';

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-center">
          {planType === 'monthly' ? 'Monthly Plan' : 'Yearly Plan'}
        </CardTitle>
        <CardDescription className="text-center">
          <span className="text-2xl font-bold">{price}</span>
          <span className="text-sm">/{planType === 'monthly' ? 'month' : 'year'}</span>
          {savings && <div className="text-green-600 font-semibold mt-1">{savings}</div>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 border rounded-md">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
          
          <div className="flex space-x-2">
            <Button
              type="submit"
              disabled={!stripe || loading}
              className="flex-1"
            >
              {loading ? 'Processing...' : `Subscribe ${price}/${planType === 'monthly' ? 'mo' : 'yr'}`}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

interface SubscriptionManagerProps {
  onClose: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ onClose }) => {
  const { user, subscription, hasActiveSubscription, refreshSubscription } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly' | null>(null);

  const handlePaymentSuccess = () => {
    refreshSubscription();
    setSelectedPlan(null);
    onClose();
  };

  if (!user) {
    return (
      <div className="text-center p-6">
        <p className="text-gray-600">Please log in to manage your subscription.</p>
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <Elements stripe={stripePromise}>
        <div className="flex justify-center p-6">
          <PaymentForm
            planType={selectedPlan}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setSelectedPlan(null)}
          />
        </div>
      </Elements>
    );
  }

  // Show current subscription status
  if (hasActiveSubscription && subscription) {
    const isTrialing = subscription.status === 'trialing';
    const endDate = new Date(isTrialing ? subscription.trial_end : subscription.current_period_end);
    const daysLeft = Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

    return (
      <div className="p-6 space-y-4">
        <div className="text-center">
          <Badge className={isTrialing ? "bg-blue-600" : "bg-green-600"}>
            {isTrialing ? 'Free Trial' : `${subscription.plan_type} Plan`}
          </Badge>
          <p className="mt-2 text-gray-600">
            {daysLeft > 0 ? `${daysLeft} days remaining` : 'Expires today'}
          </p>
        </div>

        {isTrialing && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-center">Upgrade Your Plan</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedPlan('monthly')}>
                <CardHeader className="text-center">
                  <CardTitle>Monthly</CardTitle>
                  <CardDescription>
                    <span className="text-2xl font-bold">$29</span>/month
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-green-500" onClick={() => setSelectedPlan('yearly')}>
                <CardHeader className="text-center">
                  <CardTitle>Yearly</CardTitle>
                  <CardDescription>
                    <span className="text-2xl font-bold">$290</span>/year
                    <div className="text-green-600 font-semibold mt-1">Save $58!</div>
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show pricing plans for new users
  return (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">Start with a 7-day free trial, no credit card required</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedPlan('monthly')}>
          <CardHeader className="text-center">
            <CardTitle>Monthly Plan</CardTitle>
            <CardDescription>
              <span className="text-3xl font-bold">$29</span>
              <span className="text-lg">/month</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                Full access to all features
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                Unlimited memory storage
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                AI-powered interactions
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                Priority support
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-green-500 relative">
          <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-green-600">
            Best Value
          </Badge>
          <CardHeader className="text-center pt-6">
            <CardTitle>Yearly Plan</CardTitle>
            <CardDescription>
              <span className="text-3xl font-bold">$290</span>
              <span className="text-lg">/year</span>
              <div className="text-green-600 font-semibold mt-1">Save $58 (2 months free!)</div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                Everything in Monthly plan
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                2 months free
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                Priority feature requests
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                Advanced analytics
              </li>
            </ul>
          </CardContent>
          <div 
            className="absolute inset-0 cursor-pointer"
            onClick={() => setSelectedPlan('yearly')}
          />
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionManager;